import React, { useState, useEffect } from "react";
import NameInfo from "./NameInfo.jsx";
import PageHeader from "./PageHeader.jsx";

export default function Compare({ user }) {
  const [target, setTarget] = useState('');
  const [code, setCode] = useState('');
  const [selfData, setSelfData] = useState(null);
  const [otherData, setOtherData] = useState(null);
  const [mode, setMode] = useState('top10');
  const [selected, setSelected] = useState(null);
  const [meta, setMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadSelf = async () => {
    const res = await fetch(`/api/stats/${user.username}?gender=${user.gender}`);
    const data = await res.json();
    setSelfData(data);
  };

  useEffect(() => {
    loadSelf();
    const saved = localStorage.getItem('compareTarget');
    if (saved) {
      try {
        const { target: t, code: c } = JSON.parse(saved);
        if (t) {
          setTarget(t);
          setCode(c || '');
          setTimeout(() => loadOther(), 0);
        }
      } catch {}
    }
  }, [user.gender]);

  const loadOther = async () => {
    if (!target) return;
    setIsLoading(true);
    const url = `/api/stats/${target}?code=${encodeURIComponent(code)}&gender=${user.gender}`;
    const res = await fetch(url);
    if (!res.ok) {
      setOtherData(null);
      setIsLoading(false);
      return;
    }
    const data = await res.json();
    setOtherData(data);
    localStorage.setItem('compareTarget', JSON.stringify({ target, code }));
    setIsLoading(false);
  };

  const selectName = async n => {
    setSelected(n);
    const res = await fetch(`/api/name/meta/${n}`);
    const m = await res.json();
    setMeta(m);
  };

  if (!selfData) {
    return (
      <div className="page">
        <div className="loading">Loading your data...</div>
      </div>
    );
  }

  const getRating = (d, n) => {
    const r = d.ranking[n];
    return typeof r === 'number' ? r : (r ? r.rating : 0);
  };

  const makeRankMap = d => {
    const map = {};
    d.accepted.slice()
      .sort((a, b) => getRating(d, b) - getRating(d, a))
      .forEach((n, i) => { map[n] = i + 1; });
    return map;
  };

  const rankA = makeRankMap(selfData);
  const rankB = otherData ? makeRankMap(otherData) : {};

  const overlap = otherData ? selfData.accepted.filter(n => otherData.accepted.includes(n)) : [];

  const list = overlap.slice();
  if (mode === 'top10') {
    const topA = Object.keys(rankA).filter(n => rankA[n] <= 10);
    const topB = Object.keys(rankB).filter(n => rankB[n] <= 10);
    list.splice(0, list.length, ...topA.filter(n => topB.includes(n)));
    list.sort((a, b) => (rankA[a] + rankB[a]) - (rankA[b] + rankB[b]));
  } else if (mode === 'sumelo') {
    list.sort((a, b) => (getRating(selfData, b) + getRating(otherData, b)) - (getRating(selfData, a) + getRating(otherData, a)));
  } else if (mode === 'sumrank') {
    list.sort((a, b) => (rankA[a] + rankB[a]) - (rankA[b] + rankB[b]));
  }

  return (
    <div className="page">
      <PageHeader
        title="Compare Lists 🤝"
        description="Find the perfect names you both love. Discover your shared favorites!"
      />

      <div className="card">
        <h3>👥 Compare With</h3>
        <div className="lookup-row">
          <input
            className="lookup-main"
            placeholder="Enter their username..."
            value={target}
            onChange={e => setTarget(e.target.value)}
          />
          <input
            className="lookup-code"
            placeholder="Code"
            value={code}
            onChange={e => setCode(e.target.value)}
          />
          <button onClick={loadOther} disabled={!target || isLoading}>
            {isLoading ? 'Loading...' : 'Compare'}
          </button>
        </div>
      </div>

      {otherData && (
        <>
          <div className="card">
            <h3>💕 Shared Favorites ({overlap.length} names)</h3>
            <div className="toggle-buttons">
              {[
                ['top10', '🏆 Top 10 Overlap'],
                ['sumelo', '⭐ Best Combined Rating'],
                ['sumrank', '📊 Best Combined Rank']
              ].map(([v, label]) => (
                <button
                  key={v}
                  className={mode === v ? 'active' : ''}
                  onClick={() => { setMode(v); setSelected(null); }}
                >
                  {label}
                </button>
              ))}
            </div>

            {list.length > 0 ? (
              <div className="table-scroll">
              <table className="name-table compare-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>{user.username} Rank</th>
                    <th>{user.username} Rating</th>
                    <th>{target} Rank</th>
                    <th>{target} Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(n => (
                    <tr
                      key={n}
                      className={selected === n ? 'selected' : ''}
                      onClick={() => selectName(n)}
                    >
                      <td><strong>{n}</strong></td>
                      <td>{rankA[n] || '-'}</td>
                      <td>{getRating(selfData, n)}</td>
                      <td>{rankB[n] || '-'}</td>
                      <td>{getRating(otherData, n)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ) : (
              <div className="no-data">
                No shared favorites found in this category. Try a different sorting option!
              </div>
            )}
          </div>

          {selected && meta && (
            <div className="card">
              <NameInfo name={selected} meta={meta} gender={user.gender} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

